import { effect } from '@preact/signals-react';

/**
 * A DisposableStack woven into the enclosing `createModel` factory's
 * lifecycle. `createModel` disposes every effect created during the factory
 * run when the model's `[Symbol.dispose]()` is invoked (a returned
 * `[Symbol.dispose]` member is overwritten by `createModel` itself), so an
 * effect whose *cleanup* disposes the stack is the canonical way to tie
 * arbitrary disposables — subscriptions, `DisposableBox`es — into model
 * teardown. Async work can guard against post-disposal writes by reading the
 * stack's own `disposed` getter.
 *
 * Must be called synchronously inside a `createModel` factory. Not to be
 * confused with the React `useDisposableStack` hook, which binds to component
 * lifetime instead.
 *
 * @example
 * const MyModel = createModel((init: MyModelInit) => {
 *     const disposableStack = modelDisposableStack();
 *     disposableStack.defer(init.someService.subscribe(onEvent));
 *     void loadData().then((data) => {
 *         if (disposableStack.disposed) return;
 *         ...
 *     });
 * });
 */
export function modelDisposableStack(): DisposableStack {
    const disposableStack = new DisposableStack();
    // The cleanup (not the effect body) runs at disposal time.
    effect(() => () => disposableStack.dispose());
    return disposableStack;
}
